import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../../../lib/utils';

// Toast context for managing toasts
interface Toast {
  id: string;
  message: string;
  type: 'default' | 'success' | 'error' | 'warning';
  duration: number;
}

interface ToastContextValue {
  showToast: (message: string, type?: Toast['type'], duration?: number) => string;
  removeToast: (id: string) => void;
}

const ToastContext = React.createContext<ToastContextValue | undefined>(undefined);

export const useToast = (): ToastContextValue => {
  const ctx = React.useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
};

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
        const [toasts, setToasts] = useState<Toast[]>([]);

        const showToast = useCallback(
                (message: string, type: Toast['type'] = 'default', duration = 5000) => {
                const id = Math.random().toString(36).substring(2, 9);
                setToasts((prev) => [...prev, { id, message, type, duration }]);
                return id;
        }, []);

        const removeToast = useCallback((id: string) => {
                setToasts((prev) => prev.filter((toast) => toast.id !== id));
        }, []);

        const value = useMemo<ToastContextValue>(() => ({ showToast, removeToast }), [showToast, removeToast]);

        return (
                <ToastContext.Provider value={value}>
                        {children}
                        <ToastContainer toasts={toasts} removeToast={removeToast} />
                </ToastContext.Provider>
        );
}

function ToastContainer({
  toasts,
  removeToast,
}: {
  toasts: Toast[];
  removeToast: (id: string) => void;
}): React.ReactPortal {
  return createPortal(
    <div className="fixed bottom-0 right-0 z-50 p-4 space-y-2 max-w-md">
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
      ))}
    </div>,
    document.body,
  );
}

function Toast({
  toast,
  onClose,
}: {
  toast: Toast;
  onClose: () => void;
}): JSX.Element {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, toast.duration);

    return () => clearTimeout(timer);
  }, [toast.duration, onClose]);

  return (
    <div
      className={cn(
        'flex items-center justify-between p-4 rounded-md shadow-md animate-in slide-in-from-right-full',
        {
          'bg-background border': toast.type === 'default',
          'bg-success/20 border-success text-success': toast.type === 'success',
          'bg-destructive/20 border-destructive text-destructive': toast.type === 'error',
          'bg-warning/20 border-warning text-warning': toast.type === 'warning',
        },
      )}
      role="alert"
    >
      <p>{toast.message}</p>
      <button
        onClick={onClose}
        className="ml-4 p-1 rounded-full hover:bg-accent"
        aria-label="Close"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
